import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator } from '@ngrx/store';
import { map } from 'rxjs/operators';
import {
  AccountPaymentMethod,
  GetSessionSummaryResponse,
  PaymentAccount,
} from '@arc/unified-payments-frontend/shared-payment';

// The session summary's transaction paymentAccount never carries a payment method field
// (backend PaymentAccount model only has Burn/DisplayName/NameOnAccount) — infer it from the
// displayName prefix convention ("BANK - ..." vs "CARD - ...") instead. Only Ach/Card are
// inferable this way; no current consumer's template branches on EuroDirectDebit here.
const inferPaymentMethod = (
  displayName: string | undefined,
): AccountPaymentMethod | undefined => {
  if (!displayName) {
    return undefined;
  }
  return displayName.startsWith('BANK - ')
    ? AccountPaymentMethod.Ach
    : AccountPaymentMethod.Card;
};

/**
 * Factory that creates a reusable NgRx functional effect synthesizing the transient
 * selectedPaymentAccount a live payment would have produced, from the first transaction's own
 * paymentAccount of a receipt-landing page's own session-summary load — feeds
 * selectPaymentMethodDisplayName/selectPaymentMethodType on the confirmation screen.
 *
 * Usage (POI, POA, international — each passes its own action set):
 *
 *   export const poiReceiptLandingSelectedPaymentAccountEffect =
 *     createReceiptLandingSelectedPaymentAccountEffect({
 *       loadSessionSummarySuccessAction: PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *       selectedPaymentAccountLoadedAction: PoiPaymentReceiptComponentActions.selectedPaymentAccountLoaded,
 *     });
 */
export function createReceiptLandingSelectedPaymentAccountEffect<
  A extends Action<string> & { payload: GetSessionSummaryResponse },
>(config: {
  loadSessionSummarySuccessAction: ActionCreator<string, Creator<any[], A>>;
  selectedPaymentAccountLoadedAction: ActionCreator<
    string,
    Creator<[{ selectedPaymentAccount: PaymentAccount }], Action>
  >;
}) {
  return createEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(config.loadSessionSummarySuccessAction),
        map((action) => {
          const paymentAccount =
            action.payload.payment?.transactions?.[0]?.paymentAccount;
          return config.selectedPaymentAccountLoadedAction({
            selectedPaymentAccount: {
              paymentAccountIdentifierBurn: paymentAccount?.burn,
              displayName: paymentAccount?.displayName,
              paymentMethod: inferPaymentMethod(paymentAccount?.displayName),
            },
          }) as Action;
        }),
      ),
    { functional: true },
  );
}
